import { APP_WILL_MOUNT, APP_WILL_UNMOUNT } from '../app';
import { isMobileBrowser } from '../environment/utils';
import JitsiMeetJS, { JitsiMediaDevicesEvents } from '../lib-jitsi-meet';
import { MiddlewareRegistry } from '../redux';
import { configureInitialDevices, devicePermissionsChanged, getAvailableDevices, updateDeviceList } from './actions';
import { getAudioOutputDeviceId, setAudioOutputDeviceId } from './functions';

// iOS/iPadOS(WKWebView 포함) 판정. iPadOS 는 데스크탑형 UA(Macintosh)라 maxTouchPoints 로 구분.
// iOS 는 BT 등 오디오 라우트 변경 시 미디어를 자동 일시정지하고(AVAudioSession oldDeviceUnavailable)
// 자동 재개하지 않으며, 음소거로 소리를 막으면 재생 재개(AudioTrack/Video)와 충돌하므로 별도 취급한다.
const isIOS = () => {
	const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
	return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && ((navigator.maxTouchPoints || 0) > 1));
};

let POLLING_INTERVAL = 10000; // 1초마다 체크
let pollingIntervalId = null;
let previousPermissions = {};

const checkPermissions =  async() => {
    try {
        const cameraPermission = await navigator.permissions.query({ name: 'camera' });
        const microphonePermission = await navigator.permissions.query({ name: 'microphone' });

        return {
            audio: microphonePermission.state === 'granted' ? true : false,
            video: cameraPermission.state === 'granted' ? true: false,
        };
    } catch (error) {
        return {
            audio: false,
            video: false,
        };
    }
}

// 현재 redux 에 보관된 오디오 출력장치 목록
const getOutputDevices = () => {
	const devicesState = APP.store.getState()['features/base/devices'];

	return (devicesState && devicesState.availableDevices && devicesState.availableDevices.audioOutput) || [];
};

// 주어진 장치목록(fresh)에서 'default'/'communications' 가상 항목을 제외한 실제 출력장치만 반환
const getRealAudioOutputs = devices =>
	(devices || []).filter(
		d => d.kind === 'audiooutput'
			&& d.deviceId
			&& d.deviceId !== 'default'
			&& d.deviceId !== 'communications'
	);

// jitsi enumerateDevices 콜백을 Promise 로 감싼다.
// jitsi enumerateDevices 는 void 반환(promise 미보장)이므로 await 만으로는 목록 갱신 완료를 보장 못함.
// 콜백이 실제 fresh 목록을 인자로 주므로, 콜백에서 redux 갱신 후 resolve 하여 완료를 확정한다.
const enumerateViaJitsi = () =>
	new Promise((resolve, reject) => {
		try {
			JitsiMeetJS.mediaDevices.enumerateDevices(devices => {
				APP.store.dispatch(updateDeviceList(devices));
				resolve(devices || []);
			});
		} catch (e) {
			reject(e);
		}
	});

// 최신 장치목록을 "확실히 최신값"으로 얻는다.
// 우선순위: 표준 navigator.mediaDevices.enumerateDevices()(진짜 promise) 를 fresh 비교기준으로 사용하고,
// 그 목록으로 redux 도 갱신. 표준 API 불가 시 jitsi 콜백을 Promise 로 감싸 폴백.
// → redux 목록이 아직 안 바뀌었을 가능성(stale)을 배제한다.
const enumerateFreshDevices = async () => {
	if (navigator.mediaDevices && typeof navigator.mediaDevices.enumerateDevices === 'function') {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();

			// 비교기준(반환값)과 redux 목록을 동일한 fresh 목록으로 일치시킨다
			APP.store.dispatch(updateDeviceList(devices));

			return devices || [];
		} catch (e) {
			// 표준 API 실패 → jitsi 경로로 폴백
		}
	}

	try {
		return await enumerateViaJitsi();
	} catch (e) {
		return [];
	}
};

// VOD(수업영상)/기타 <video>·<audio> 는 jitsi 가 attach 한 요소가 아닐 수 있어 sinkId 자동 전파 대상이 아님.
// 페이지 내 setSinkId 가능한 모든 미디어 요소에 직접 재적용(각 요소 실패는 개별 무시).
// (라이브 강의의 jitsi 원격 <audio> 는 setAudioOutputDevice 로 이미 자동 전파되지만 폴백으로도 안전)
const applySinkToMediaElements = deviceId => {
	let els = [];

	try {
		els = Array.prototype.slice.call(document.querySelectorAll('video, audio'));
	} catch (e) {
		els = [];
	}

	els.forEach(el => {
		if (el && typeof el.setSinkId === 'function') {
			try {
				const p = el.setSinkId(deviceId);

				if (p && typeof p.catch === 'function') {
					p.catch(() => {});
				}
			} catch (e) {
				// 개별 요소 실패는 무시
			}
		}
	});
};

// 라이브 출력 차단 상태(블루투스 등 사용 중이던 출력장치 제거로 내장 스피커 폴백 시 소리를 막았는지).
let outputBlocked = false;

// 재생 중 미디어 요소의 음소거를 일괄 설정.
// muted 는 play() 재시도에도 유지되므로(jitsi AudioTrack 이 최대 7회 재생 재시도) 확실히 소리를 막는다.
// (pause 는 재시도/넛지로 곧 풀리므로 차단 수단으로 부적합)
const setMediaElementsMuted = muted => {
	let els = [];

	try {
		els = Array.prototype.slice.call(document.querySelectorAll('video, audio'));
	} catch (e) {
		els = [];
	}

	els.forEach(el => {
		if (!el) {
			return;
		}

		try {
			el.muted = muted;
		} catch (e) {
			// 개별 요소 실패는 무시
		}
	});
};

// 블루투스 등 사용 중이던 출력장치가 제거되어 내장 스피커로 폴백될 때,
// 라이브 소리가 갑자기 내장 스피커로 크게 송출되지 않도록 출력을 차단(음소거)하고 안내한다.
// 새 출력장치가 연결되면(unblockMediaOutput) 자동으로 해제된다.
const blockMediaOutput = () => {
	if (outputBlocked) {
		return;
	}

	outputBlocked = true;
	setMediaElementsMuted(true);

	try {
		APP.UI.alertMessage(
			'블루투스 연결이 해제되어 라이브 소리가 일시 중지되었습니다. 기기를 다시 연결하면 자동으로 재생됩니다.',
			undefined,
			false,
			{ autoClose: 4000 }
		);
	} catch (e) {
		// 알림 실패는 무시(차단 자체는 유효)
	}
};

// 새 출력장치 연결 등으로 다시 소리를 낼 수 있게 되면 차단 해제(음소거 복구).
const unblockMediaOutput = () => {
	if (!outputBlocked) {
		return;
	}

	outputBlocked = false;
	setMediaElementsMuted(false);
};

// deviceChange 재진입/중복 실행 가드 — DEVICE_LIST_CHANGED 와 표준 devicechange 가 동시에 와도 안전
let deviceChangeInFlight = false;
let deviceChangePending = false;

const deviceChangeImpl = async () => {
	// 변경 전 출력장치 스냅샷 (블루투스 등 신규 연결 감지용)
	const prevOutputs = getOutputDevices();
	const prevIds = new Set(prevOutputs.map(d => d.deviceId));

	// 확실히 최신인 목록을 fresh 로 획득(redux stale 경쟁 제거)
	const freshDevices = await enumerateFreshDevices();

	// 출력장치 전환 미지원 환경(iOS Safari 등)은 OS 가 처리하므로 스킵
	if (!JitsiMeetJS.mediaDevices.isDeviceChangeAvailable('output')) return;
	// 초기 목록 채움(비교 대상 없음)일 때는 사용자가 고른 기본값을 덮어쓰지 않도록 자동 전환 보류
	if (prevOutputs.length === 0) return;

	const { dispatch } = APP.store;
	// 'default'/'communications' 가상 항목 제외, fresh 목록에서 실제 장치만 비교
	const realOutputs = getRealAudioOutputs(freshDevices);

	// 수업 중 새로 연결/전환된 출력장치(블루투스 이어폰 등) → 즉시 전환
	const addedDevice = realOutputs.find(d => !prevIds.has(d.deviceId));

	let targetId;

	if (addedDevice) {
		targetId = addedDevice.deviceId;
		// 새 출력장치 연결(블루투스 재연결/유선 이어폰 등) → 이전 차단 해제하고 소리 복구
		unblockMediaOutput();
	} else {
		// 신규 장치는 없지만 사용 중이던 출력장치가 제거된 경우 → 기본 출력으로 폴백
		const currentId = getAudioOutputDeviceId();
		const stillExists = freshDevices.some(d => d.deviceId === currentId);

		if (currentId && currentId !== 'default' && !stillExists) {
			targetId = 'default';
			// 모바일(Android 등): 사용 중이던 실제 출력장치(블루투스)가 제거되어 내장 스피커로 폴백되는
			// 명확한 경우 → 라이브 소리가 갑자기 스피커로 새어나가지 않도록 차단(음소거)+안내.
			// ★iOS/iPad 제외: iOS 는 라우트 변경 시 미디어를 자동 일시정지하고 우리가 재생을 재개하므로,
			//   여기서 음소거하면 재개돼도 소리가 안 나 "재연결해도 무음"이 된다(케이스 ①). → 재생 재개에 위임.
			if (isMobileBrowser() && !isIOS()) {
				blockMediaOutput();
			}
		}
	}

	if (!targetId) return;

	// jitsi 가 attach 한 원격 오디오 요소는 setAudioOutputDevice 가 sinkId 자동 재적용
	await setAudioOutputDeviceId(targetId, dispatch).catch(() => {});
	// VOD <video> 등 jitsi 비관리 미디어 요소에도 직접 sinkId 재적용
	applySinkToMediaElements(targetId);
};

// 외부 진입점: 중복/재진입을 가드. 실행 중 들어온 요청은 1회로 합쳐 마지막에 재실행(최신 상태 반영)
const deviceChange = async () => {
	if (deviceChangeInFlight) {
		deviceChangePending = true;

		return;
	}

	deviceChangeInFlight = true;

	try {
		await deviceChangeImpl();
	} catch (e) {
		// 감지/전환 실패가 앱을 막지 않도록 무시
	} finally {
		deviceChangeInFlight = false;

		if (deviceChangePending) {
			deviceChangePending = false;
			// 실행 중 누적된 추가 변경을 한 번 더 처리
			deviceChange();
		}
	}
};

// 표준 navigator.mediaDevices devicechange 리스너 등록/해제 (지원 환경에서만)
const supportsNativeDeviceChange = () =>
	typeof navigator !== 'undefined'
	&& navigator.mediaDevices
	&& typeof navigator.mediaDevices.addEventListener === 'function';

const addNativeDeviceChangeListener = () => {
	if (supportsNativeDeviceChange()) {
		navigator.mediaDevices.addEventListener('devicechange', deviceChange);
	}
};

const removeNativeDeviceChangeListener = () => {
	if (supportsNativeDeviceChange()) {
		navigator.mediaDevices.removeEventListener('devicechange', deviceChange);
	}
};

const startPermissionPolling = async (onPermissionsChanged) => {
	const polling = async () => {
        const currentPermissions = await checkPermissions();

        // 권한 상태 변경 감지
        if (
            currentPermissions?.video !== previousPermissions.video ||
            currentPermissions?.audio !== previousPermissions.audio
        ) {

            previousPermissions = currentPermissions;
			deviceChange();
			APP.store.dispatch(devicePermissionsChanged(currentPermissions));
			APP.store.dispatch(configureInitialDevices());

		}
    }
    
	polling();
    pollingIntervalId = setInterval(polling, POLLING_INTERVAL);
};

const stopPermissionPolling = () => {

    if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
        pollingIntervalId = null;
    }
};

const togglePolling = () => {
	if (document.visibilityState === 'hidden') {
		stopPermissionPolling();
	} else if (document.visibilityState === 'visible') {
		startPermissionPolling(devicePermissionsChanged);
	}
}

const changeSelectionTime = (time) => {
	stopPermissionPolling();
	POLLING_INTERVAL = time;
	startPermissionPolling(devicePermissionsChanged);
}

/**
 * A listener for device permissions changed reported from lib-jitsi-meet.
 */

MiddlewareRegistry.register(store => next => action => {
	switch (action.type) {
		case APP_WILL_MOUNT:
			if (store.getState()['features/base/app'].app === true) return;


			document.addEventListener('visibilitychange', togglePolling);


			// jitsi lib 의 DEVICE_LIST_CHANGED 감지
			JitsiMeetJS.mediaDevices.addEventListener(JitsiMediaDevicesEvents.DEVICE_LIST_CHANGED, deviceChange);
			// 브라우저 표준 devicechange 도 함께 감지(이중 감지). 두 경로가 동시에 와도 deviceChange 가 재진입 가드
			addNativeDeviceChangeListener();

			// 최소 1회 목록을 채워 자동전환 비교 기준(prevOutputs)이 생기도록 초기화
			// (초기 목록이 영영 안 채워지면 prevOutputs.length===0 가드가 자동전환을 영구 차단)
			store.dispatch(getAvailableDevices());

			APP.eventManager.subscribe('UPDATE_DEVICE_SELECTION_TIME', changeSelectionTime);

			break;

		case APP_WILL_UNMOUNT:
			stopPermissionPolling();

			document.removeEventListener('visibilitychange', togglePolling);
			// 리스너 정리(누수 방지)
			JitsiMeetJS.mediaDevices.removeEventListener(JitsiMediaDevicesEvents.DEVICE_LIST_CHANGED, deviceChange);
			removeNativeDeviceChangeListener();
			APP.eventManager.unsubscribe('UPDATE_DEVICE_SELECTION_TIME', changeSelectionTime);

			break;

	}

	return next(action);
});
